/**
 * The Review tab's landing screen (docs/04-screen-specs.md E1).
 *
 * This is the screen the limit sheet sends people to, so it has to be worth arriving
 * at. It is always free and always available: the entire soft-paywall argument rests on
 * a learner who has run out of speaking time still having something real to do.
 */

import { Button, EmptyState, ErrorState, Skeleton } from '@saysooner/ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { useApi, useApp, useCourseId } from '../app-context.js';
import { qk, retryIf } from '../lib/client.js';

export interface ReviewHubScreenProps {
  readonly onNavigate: (route: string) => void;
}

export function ReviewHubScreen({ onNavigate }: ReviewHubScreenProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();

  const queue = useQuery({
    queryKey: qk.reviewQueue(courseId, true),
    queryFn: () =>
      api.call('getReviewQueue', { query: { courseId, limit: 40, allowSpoken: true } }),
  });

  const mistakes = useQuery({
    queryKey: qk.mistakes(courseId, 'open'),
    queryFn: () => api.call('listMistakes', { query: { courseId, status: 'open', limit: 1 } }),
  });

  // Recorded once both counts are known. This is where a learner lands after hitting
  // the speaking cap, so how often it is reached and what was waiting are the numbers
  // that say whether "always free, always scored" is actually load-bearing.
  const due = queue.data?.dueTotal;
  const openMistakes = mistakes.data?.openCount;
  useEffect(() => {
    if (due === undefined || openMistakes === undefined) return;
    track('review_home_view', {
      due,
      mistakes: openMistakes,
      // Leeches are a server-side classification; the queue response does not carry a
      // count, and a derived guess here would not be the same number the server means.
      leeches: null,
    });
  }, [due, openMistakes, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Review</h1>
      {/* Stated plainly, because this is where someone lands after hitting the daily
          speaking cap and needs to know this part is not about to stop too. */}
      <p className="vl-page__lede">Always free, always scored, and it never uses speaking time.</p>

      {queue.isPending && <Skeleton lines={4} label="Loading your review queue" />}
      {queue.error !== null && (
        <ErrorState
          title="We couldn't load your queue"
          {...retryIf(queue.error, () => void queue.refetch())}
          alternative={{
            label: 'Work through mistakes instead',
            onClick: () => onNavigate('/review/mistakes'),
          }}
        />
      )}

      {queue.data !== undefined && (
        <>
          {queue.data.dueTotal === 0 ? (
            <EmptyState
              title="Nothing due"
              body="Your next cards are scheduled. Coming back before they are due does not help them stick."
              action={{ label: 'Start a conversation', onClick: () => onNavigate('/talk') }}
            />
          ) : (
            <section className="vl-review-hub__due">
              <p className="vl-review-hub__count">
                {queue.data.dueTotal} due · about {queue.data.estimatedMinutes} min
              </p>
              {queue.data.newIntroduced > 0 && (
                <p className="vl-review-hub__new">
                  {queue.data.newIntroduced} new {queue.data.newIntroduced === 1 ? 'item' : 'items'}{' '}
                  mixed in
                </p>
              )}
              <Button onClick={() => onNavigate('/review/session')}>Start reviewing</Button>
            </section>
          )}

          {/* The forecast, so the schedule is legible rather than arbitrary. A learner
              who can see next week's load trusts the spacing. */}
          {queue.data.forecast.length > 0 && (
            <section>
              <h2>Coming up</h2>
              <ol className="vl-forecast" aria-label="Cards due over the next days">
                {queue.data.forecast.map((day) => (
                  <li key={day.dayOffset}>
                    <span className="vl-forecast__label">
                      {day.dayOffset === 0
                        ? 'Today'
                        : day.dayOffset === 1
                          ? 'Tomorrow'
                          : `In ${day.dayOffset} days`}
                    </span>
                    <span className="vl-forecast__count">{day.count}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </>
      )}

      <nav className="vl-review-hub__links">
        <Button variant="secondary" onClick={() => onNavigate('/review/mistakes')}>
          Your mistakes
          {mistakes.data !== undefined && mistakes.data.openCount > 0 && (
            <> · {mistakes.data.openCount} open</>
          )}
        </Button>
        <Button variant="secondary" onClick={() => onNavigate('/review/clinic')}>
          Pronunciation clinic
        </Button>
        <Button variant="ghost" onClick={() => onNavigate('/workbook')}>
          Written workbook
        </Button>
      </nav>
    </main>
  );
}
